import { useEffect, useState } from "react";

import Navbar from "../components/Navbar";
import DashboardChart from "../components/DashboardChart";

import api from "../services/api";
import { exportToExcel } from "../utils/exportExcel";

export default function DistrictDashboard() {
  const [data, setData] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [blockFilter, setBlockFilter] = useState("");

  const [fromMonth, setFromMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [toMonth, setToMonth] = useState(new Date().toISOString().slice(0, 7));

  const [viewMode, setViewMode] = useState("month");

  const [editingRow, setEditingRow] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlocks();
    loadData();
  }, [fromMonth, toMonth, viewMode]);

  /* ============================
     LOAD BLOCKS
  ============================ */

  const loadBlocks = async () => {
    try {
      const res = await api.get("/admin/blocks");
      setBlocks(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  /* ============================
     LOAD DASHBOARD DATA
  ============================ */

  const loadData = async () => {
    try {
      const res = await api.get(
        `/dashboard/district?from=${fromMonth}&to=${toMonth}&mode=${viewMode}`
      );

      setData(res.data);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  /* ============================
     FILTER DATA
  ============================ */

  const filtered = blockFilter
    ? data.filter((d) => d.block === blockFilter)
    : data;

  /* ============================
     SUMMARY
  ============================ */

  const totalBlocks = new Set(filtered.map((d) => d.block)).size;

  const totalDepartments = new Set(filtered.map((d) => d.department)).size;

  const totalSchemes = new Set(filtered.map((d) => d.scheme)).size;

  const totalValue = filtered.reduce(
    (sum, row) => sum + Number(row.value ?? 0),
    0
  );

  /* ============================
     DELETE ENTRY
  ============================ */

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;

    try {
      await api.delete(`/data/entry/${id}`);

      loadData();
    } catch (err) {
      console.log(err);
    }
  };

  /* ============================
     EDIT ENTRY
  ============================ */

  const startEdit = (row) => {
    const rowKey = `${row.block}-${row.department}-${row.scheme}-${row.entry_date}`;
    setEditingRow({
      id: row.id || null,
      rowKey,
      block_id: row.block_id,
      scheme_id: row.scheme_id,
      entry_date: row.entry_date,
      value: row.value ?? 0,
    });
  };

  const saveEdit = async () => {
    const value = Number(editingRow.value);

    if (isNaN(value)) {
      alert("Enter valid number");
      return;
    }

    /* =========================
     FIX DATE (IMPORTANT)
  ========================= */

    let entryDate;

    if (editingRow.entry_date) {
      const d = new Date(editingRow.entry_date);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");

      entryDate = `${year}-${month}-01`; // ✅ ALWAYS FIRST DAY
    } else {
      entryDate = `${fromMonth}-01`;
    }

    console.log("Saving with date:", entryDate);

    try {
      if (editingRow.id) {
        await api.put(`/data/entry/${editingRow.id}`, {
          value: value,
        });
      } else {
        await api.post(`/data/entry`, {
          block_id: Number(editingRow.block_id),
          scheme_id: Number(editingRow.scheme_id),
          entry_date: entryDate, // ✅ FIXED
          value: value,
        });
      }

      setEditingRow(null);
      loadData();
    } catch (err) {
      console.log(err);
      alert("Failed to save");
    }
  };

  const cancelEdit = () => {
    setEditingRow(null);
  };

  /* ============================
     UI
  ============================ */

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto p-10">
        <h2 className="text-2xl font-bold mb-6">
          District Monitoring Dashboard
        </h2>

        {/* FILTERS */}

        <div className="flex gap-4 mb-6 flex-wrap">
          <div>
            <label className="text-sm text-gray-600">From Month</label>

            <input
              type="month"
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">To Month</label>

            <input
              type="month"
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">View Mode</label>

            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border p-2 rounded ml-2"
            >
              <option value="month">Month Wise</option>
              <option value="cumulative">Cumulative</option>
            </select>
          </div>

          <select
            className="border p-2"
            value={blockFilter}
            onChange={(e) => setBlockFilter(e.target.value)}
          >
            <option value="">All Blocks</option>

            {blocks.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => exportToExcel(filtered)}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export Excel
          </button>
        </div>

        {/* SUMMARY */}

        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 shadow rounded">
            <p className="text-gray-500">Blocks</p>
            <h3 className="text-3xl font-bold">{totalBlocks}</h3>
          </div>

          <div className="bg-white p-6 shadow rounded">
            <p className="text-gray-500">Departments</p>
            <h3 className="text-3xl font-bold">{totalDepartments}</h3>
          </div>

          <div className="bg-white p-6 shadow rounded">
            <p className="text-gray-500">Schemes</p>
            <h3 className="text-3xl font-bold">{totalSchemes}</h3>
          </div>

          <div className="bg-white p-6 shadow rounded">
            <p className="text-gray-500">Beneficiaries</p>
            <h3 className="text-3xl font-bold">{totalValue}</h3>
          </div>
        </div>

        {/* CHART */}

        <DashboardChart
          data={filtered}
          blockFilter={blockFilter}
          fromMonth={fromMonth}
          toMonth={toMonth}
        />

        {loading && <p>Loading dashboard...</p>}

        {/* TABLE */}

        <div className="mt-8 overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-3">Block</th>
                <th className="p-3">Department</th>
                <th className="p-3">Scheme</th>
                <th className="p-3">Month</th>
                <th className="p-3">Beneficiaries</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((row) => {
                const rowKey = `${row.block}-${row.department}-${row.scheme}-${row.entry_date}`;

                return (
                  <tr
                    key={`${row.block_id}-${row.scheme_id}-${row.entry_date}`}
                    className="border-t"
                  >
                    <td className="p-3">{row.block}</td>
                    <td className="p-3">{row.department}</td>
                    <td className="p-3">{row.scheme}</td>

                    <td className="p-3">
                      {viewMode === "cumulative"
                        ? `${fromMonth} → ${toMonth}`
                        : row.entry_date
                        ? new Date(row.entry_date).toLocaleDateString("en-IN", {
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </td>

                    <td className="p-3">
                      {editingRow && editingRow.rowKey === rowKey ? (
                        <input
                          type="number"
                          min="0"
                          value={editingRow.value ?? 0}
                          onChange={(e) =>
                            setEditingRow({
                              ...editingRow,
                              value: e.target.value === "" ? 0 : e.target.value,
                            })
                          }
                          className="border p-1 w-24"
                        />
                      ) : (
                        row.value ?? 0
                      )}
                    </td>

                    <td className="p-3 flex gap-2">
                      {viewMode === "month" &&
                        (editingRow && editingRow.rowKey === rowKey ? (
                          <>
                            <button
                              onClick={saveEdit}
                              className="bg-blue-600 text-white px-2 py-1 rounded"
                            >
                              Save
                            </button>

                            <button
                              onClick={cancelEdit}
                              className="bg-gray-400 text-white px-2 py-1 rounded"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(row)}
                              className="bg-yellow-500 text-white px-2 py-1 rounded"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => deleteEntry(row.id)}
                              className="bg-red-600 text-white px-2 py-1 rounded"
                            >
                              Delete
                            </button>
                          </>
                        ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
